import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Minus, Plus, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import {
  cpPercentHeatmapColor,
  endlessBarProps,
  GRAPHIC_EDITOR_CP_MAX,
  GRAPHIC_EDITOR_GRAB_FRAC,
  GRAPHIC_EDITOR_INTENSITY_FRAC,
  graphicEditorCp100LineBottomPercentOfColumn,
  graphicEditorEffectiveCp,
  graphicEditorIntensityFillPercent,
  runningSegmentBarColorClass,
  runningSegmentUsesCpHeatmapBarColor,
  segmentRowToVizSplit,
  splitTitleParts,
  splitWeight,
  splitWeightPace,
} from '../../utils/runningWorkoutVizShared';
import {
  effectiveDistanceDisplayUnit,
  flipAllDistanceSegmentUnits,
  formatDistanceFromMeters,
  formatSplitDurationLabel,
  metersForSegmentDistanceLine,
  snapDistanceMeters,
} from '../../utils/runningSegmentLabels';
import {
  bumpCpPercentString,
  mergeCpPercentDragWithRangeShift,
  nextCpPercentFromInput,
  patchCpPercentWithFollowingRange,
} from '../../utils/runningCpInput';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { TooltipProvider } from '../ui/tooltip';
import SegmentMeasureEditorFields from './SegmentMeasureEditorFields';

const CP_MIN = 0;
const CP_MAX = GRAPHIC_EDITOR_CP_MAX;
const CLICK_MAX_MOVE = 5;
const CLICK_MAX_MS = 450;
/** When Shift is held during duration or %CP drag, snap changes to multiples of this step. */
const DRAG_SNAP_STEP = 5;

/** Nearest multiple of step (ties round up at .5 per Math.round). */
function nearestStep(value, step) {
  return Math.round(value / step) * step;
}

const MARQUEE_DRAG_PX = 4;
const ZOOM_LEVELS = [1, 1.5, 2, 3, 4];

function nextGraphicSegmentLocalKey() {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function cloneSegmentRowForPaste(row) {
  const copy = { ...row, localKey: nextGraphicSegmentLocalKey() };
  delete copy.id;
  return copy;
}

/** Segment column indices whose horizontal span overlaps [clientX1, clientX2] on the chart row. */
function segmentIndicesOverlappingClientXRange(chartEl, clientX1, clientX2, weights, sumW) {
  if (!chartEl || !weights.length) return [];
  const r = chartEl.getBoundingClientRect();
  const lo = Math.min(clientX1, clientX2);
  const hi = Math.max(clientX1, clientX2);
  const out = [];
  let acc = 0;
  for (let i = 0; i < weights.length; i += 1) {
    const left = r.left + (acc / sumW) * r.width;
    const right = r.left + ((acc + weights[i]) / sumW) * r.width;
    if (right >= lo && left <= hi) out.push(i);
    acc += weights[i];
  }
  return out;
}

function segmentIndexAtClientX(chartEl, clientX, weights, sumW) {
  if (!chartEl || !weights.length) return null;
  const r = chartEl.getBoundingClientRect();
  if (clientX < r.left || clientX > r.right) return null;
  const f = (clientX - r.left) / Math.max(1, r.width);
  let acc = 0;
  for (let i = 0; i < weights.length; i += 1) {
    acc += weights[i] / sumW;
    if (f < acc) return i;
  }
  return weights.length - 1;
}

/** Old index → new index after removing `removedSet`, or null if that old index was removed. */
function remapIndexAfterRemove(oldIdx, removedSet) {
  if (removedSet.has(oldIdx)) return null;
  let d = 0;
  for (const r of removedSet) {
    if (r < oldIdx) d += 1;
  }
  return oldIdx - d;
}

function chartCollisionDetection(args) {
  const first = pointerWithin(args);
  if (first.length > 0) return first;
  return closestCenter(args);
}

/** %CP / min / max with − / + steppers (graphic Split details popover). */
function GraphicCpSpinRow({ label, field, seg, applyPatch, t, resolvedCpWatts }) {
  const value =
    field === 'cp' ? seg.cp_percent : field === 'min' ? seg.cp_percent_min : seg.cp_percent_max;
  const ariaField =
    field === 'cp'
      ? t('runningExercise.cpPercent')
      : field === 'min'
        ? t('runningExercise.cpRangeMin')
        : t('runningExercise.cpRangeMax');

  const pctForWatts =
    field === 'cp'
      ? Math.round(graphicEditorEffectiveCp(seg))
      : (() => {
          const raw = field === 'min' ? seg.cp_percent_min : seg.cp_percent_max;
          const n = raw === '' || raw == null ? NaN : Number(String(raw).trim().replace(',', '.'));
          return Number.isFinite(n) ? Math.round(n) : null;
        })();
  const wattsInfo =
    resolvedCpWatts != null &&
    Number.isFinite(Number(resolvedCpWatts)) &&
    Number(resolvedCpWatts) > 0 &&
    pctForWatts != null
      ? wattsFromCpBase(resolvedCpWatts, pctForWatts)
      : null;

  const step = (delta) => {
    if (field === 'cp') {
      const next = bumpCpPercentString(seg.cp_percent, delta);
      const partial = patchCpPercentWithFollowingRange(seg, next);
      if (partial) applyPatch(partial);
      return;
    }
    if (field === 'min') {
      applyPatch({ cp_percent_min: bumpCpPercentString(seg.cp_percent_min, delta) });
      return;
    }
    applyPatch({ cp_percent_max: bumpCpPercentString(seg.cp_percent_max, delta) });
  };

  const spinSideClass = 'h-8 w-7 shrink-0 px-0';
  const spinSideSpacer = () => (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={cn(spinSideClass, 'pointer-events-none invisible')}
      tabIndex={-1}
      aria-hidden
      disabled
    >
      <Minus className="h-3.5 w-3.5" />
    </Button>
  );

  return (
    <div className="min-w-0 space-y-1">
      <div className="flex w-full min-w-0 items-end gap-1">
        {spinSideSpacer()}
        <p
          className="min-w-0 flex-1 text-center text-[10px] leading-tight text-muted-foreground tabular-nums"
          title={
            wattsInfo == null && resolvedCpWatts == null
              ? t('runningExercise.setCpHint')
              : undefined
          }
        >
          {wattsInfo != null
            ? t('runningExercise.splitDetailPowerWatts', { n: wattsInfo })
            : t('runningExercise.graphicNoDuration')}
        </p>
        {spinSideSpacer()}
      </div>
      <div className="flex w-full min-w-0 items-center gap-1">
        {spinSideSpacer()}
        <Label className="flex-1 text-center text-[10px]">{label}</Label>
        {spinSideSpacer()}
      </div>
      <div className="flex w-full min-w-0 items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={spinSideClass}
          aria-label={`${t('runningExercise.cpStepDown')} (${ariaField})`}
          onClick={() => step(-1)}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <Input
          type="text"
          inputMode="numeric"
          className="h-8 min-w-[3.5rem] flex-1 px-2 text-center text-xs tabular-nums sm:min-w-[4rem]"
          value={value}
          onChange={(e) => {
            if (field === 'cp') {
              const partial = patchCpPercentWithFollowingRange(seg, e.target.value);
              if (partial == null) return;
              applyPatch(partial);
              return;
            }
            if (field === 'min') {
              const next = nextCpPercentFromInput(e.target.value);
              if (next === undefined) return;
              applyPatch({ cp_percent_min: next });
              return;
            }
            const next = nextCpPercentFromInput(e.target.value);
            if (next === undefined) return;
            applyPatch({ cp_percent_max: next });
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={spinSideClass}
          aria-label={`${t('runningExercise.cpStepUp')} (${ariaField})`}
          onClick={() => step(1)}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function SegmentDetailForm({
  seg,
  idx,
  t,
  setSegments,
  duplicateSegment,
  onDeleteGraphicSegments,
  onClose,
  distanceUnits,
  thresholdPaceSecPerKm,
  resolvedCpWatts,
}) {
  const patch = (partial) =>
    setSegments((prev) => prev.map((r, i) => (i === idx ? { ...r, ...partial } : r)));

  return (
    <div className="flex flex-col gap-3 text-popover-foreground">
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-x-4 sm:gap-y-2">
        <GraphicCpSpinRow
          label={t('runningExercise.cpPercent')}
          field="cp"
          seg={seg}
          applyPatch={patch}
          t={t}
          resolvedCpWatts={resolvedCpWatts}
        />
        <GraphicCpSpinRow
          label={t('runningExercise.cpRangeMin')}
          field="min"
          seg={seg}
          applyPatch={patch}
          t={t}
          resolvedCpWatts={resolvedCpWatts}
        />
        <GraphicCpSpinRow
          label={t('runningExercise.cpRangeMax')}
          field="max"
          seg={seg}
          applyPatch={patch}
          t={t}
          resolvedCpWatts={resolvedCpWatts}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-0">
        <div className="min-h-0 min-w-0">
          <SegmentMeasureEditorFields
            seg={seg}
            onPatch={patch}
            distanceUnits={distanceUnits}
            thresholdPaceSecPerKm={thresholdPaceSecPerKm}
            t={t}
            onFlipAllDistanceUnits={() =>
              setSegments((prev) => flipAllDistanceSegmentUnits(prev, distanceUnits === 'miles' ? 'miles' : 'km'))
            }
            inlineActions={
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 shrink-0 gap-1 whitespace-nowrap px-2"
                  onClick={() => duplicateSegment(idx)}
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" />
                  <span className="max-w-[9rem] truncate sm:max-w-none">{t('runningExercise.duplicateSplit')}</span>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 px-0 text-destructive"
                  aria-label={t('common.delete')}
                  onClick={() => {
                    onDeleteGraphicSegments(idx);
                    onClose();
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            }
          />
        </div>
        <div className="min-h-0 min-w-0 space-y-1">
          <Label className="text-xs">{t('runningExercise.segClass')}</Label>
          <Select
            value={seg.run_intensity_class}
            onValueChange={(v) =>
              patch({
                run_intensity_class: v,
                ...(v === 'rest' ? { cp_percent: 60, cp_percent_min: '', cp_percent_max: '' } : {}),
              })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[10500]">
              <SelectItem value="warmup">warmup</SelectItem>
              <SelectItem value="work">work</SelectItem>
              <SelectItem value="rest">rest</SelectItem>
              <SelectItem value="cooldown">cooldown</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id={`endless-${idx}`}
          checked={!!seg.endless}
          onCheckedChange={(v) => patch({ endless: v })}
        />
        <Label htmlFor={`endless-${idx}`} className="text-xs cursor-pointer" title={t('runningExercise.endlessHint')}>
          {t('runningExercise.endless')}
        </Label>
      </div>
    </div>
  );
}

function wattsFromCpBase(cpBase, pct) {
  if (cpBase == null || !Number.isFinite(Number(cpBase)) || Number(cpBase) <= 0) return null;
  const p = Number(pct);
  if (!Number.isFinite(p)) return null;
  return Math.round((Number(cpBase) * p) / 100);
}

/** One-line label above graphic bars: main %CP plus optional min–max range; optional absolute watts when CP is known. */
function formatGraphicBarCpLabel(seg, resolvedCpWatts) {
  const eff = Math.round(graphicEditorEffectiveCp(seg));
  const minRaw = seg.cp_percent_min;
  const maxRaw = seg.cp_percent_max;
  const minN = minRaw === '' || minRaw == null ? NaN : Number(String(minRaw).trim().replace(',', '.'));
  const maxN = maxRaw === '' || maxRaw == null ? NaN : Number(String(maxRaw).trim().replace(',', '.'));
  const hasRange = Number.isFinite(minN) && Number.isFinite(maxN);
  let text = hasRange ? `${eff}% (${Math.round(minN)}–${Math.round(maxN)})` : `${eff}%`;
  if (resolvedCpWatts != null) {
    const wMain = wattsFromCpBase(resolvedCpWatts, eff);
    if (wMain != null) {
      if (hasRange) {
        const wMin = wattsFromCpBase(resolvedCpWatts, Math.round(minN));
        const wMax = wattsFromCpBase(resolvedCpWatts, Math.round(maxN));
        if (wMin != null && wMax != null) {
          text = `${text} · ${wMain} W (${wMin}–${wMax} W)`;
        } else {
          text = `${text} · ${wMain} W`;
        }
      } else {
        text = `${text} · ${wMain} W`;
      }
    }
  }
  return text;
}

function graphicBarFooterParts(seg, t, thresholdPaceSecPerKm, distanceUnits) {
  const viz = segmentRowToVizSplit(seg);
  const mode = viz.split_measure_mode === 'distance' ? 'distance' : 'duration';
  let line1;
  let line2;
  if (mode === 'distance' && viz.distance_m != null && viz.distance_m > 0) {
    const footerU = effectiveDistanceDisplayUnit(seg, distanceUnits === 'miles' ? 'miles' : 'km');
    line1 =
      formatDistanceFromMeters(viz.distance_m, footerU === 'miles' ? 'miles' : 'km', t) ||
      t('runningExercise.graphicNoDuration');
    line2 = viz.duration_sec > 0 ? formatSplitDurationLabel(viz.duration_sec, t) : null;
  } else {
    if (viz.duration_sec > 0) {
      line1 = formatSplitDurationLabel(viz.duration_sec, t);
    } else if (viz.distance_m != null && viz.distance_m > 0) {
      line1 = t('runningExercise.graphicMeters', { n: Math.round(viz.distance_m) });
    } else {
      line1 = t('runningExercise.graphicNoDuration');
    }
    line2 = formatDistanceFromMeters(
      metersForSegmentDistanceLine(seg, thresholdPaceSecPerKm),
      distanceUnits,
      t
    );
  }
  const eff = graphicEditorEffectiveCp(seg);
  const cp = t('runningExercise.graphicCpValue', { n: Math.round(eff) });
  return { line1, line2, cp };
}

/** Match graphic fill / labels (graphicEditorEffectiveCp); avoids empty %CP behaving as 85 in drag. */
function readCp(seg) {
  return Math.round(graphicEditorEffectiveCp(seg));
}

function applyWeightToRow(row, newWeight, shiftSnap, userDistanceUnitDefault, thresholdPace) {
  const viz = segmentRowToVizSplit(row);
  const mode = viz.split_measure_mode === 'distance' ? 'distance' : 'duration';
  const w = Math.max(1, Math.round(newWeight));
  if (mode === 'distance') {
    const pace = splitWeightPace(viz, thresholdPace);
    const rawM = Math.max(10, Math.round((w / pace) * 1000));
    const displayU = effectiveDistanceDisplayUnit(row, userDistanceUnitDefault);
    const snapped = snapDistanceMeters(rawM, displayU, !!shiftSnap);
    return { ...row, distance_m: snapped };
  }
  return { ...row, duration_sec: w };
}

/** All selected indices when the dragged column is selected; otherwise just that column. */
function fullResizeGroup(selectedSet, idx, n) {
  if (!selectedSet?.has?.(idx)) return [idx];
  return [...selectedSet]
    .filter((i) => i >= 0 && i < n)
    .sort((a, b) => a - b);
}

/**
 * Integer-split `targetSum` across group indices proportionally to base weights.
 * @param {number[]} baseW
 * @param {number[]} groupIndices
 * @param {number} targetSum
 */
function redistributeGroupWeights(baseW, groupIndices, targetSum) {
  const sorted = [...groupIndices].sort((a, b) => a - b);
  const sum0 = sorted.reduce((s, i) => s + baseW[i], 0);
  const target = Math.max(sorted.length, Math.round(targetSum));
  const w = baseW.slice();
  if (sum0 <= 0) {
    const each = Math.max(1, Math.floor(target / sorted.length));
    let acc = 0;
    sorted.forEach((i, k) => {
      if (k === sorted.length - 1) w[i] = Math.max(1, target - acc);
      else {
        w[i] = each;
        acc += each;
      }
    });
    return w;
  }
  let acc = 0;
  sorted.forEach((i, k) => {
    if (k === sorted.length - 1) {
      w[i] = Math.max(1, target - acc);
    } else {
      const part = Math.round((target * baseW[i]) / sum0);
      const wi = Math.max(1, Math.min(part, target - acc - (sorted.length - k - 1)));
      w[i] = wi;
      acc += wi;
    }
  });
  return w;
}

/** Change only `groupIndices` weights; others unchanged → total workout duration grows or shrinks. */
function applyGroupDurationDelta(baseW, groupIndices, deltaSigned, shiftSnap, segmentRows) {
  const sorted = [...groupIndices].sort((a, b) => a - b);
  const sumG = sorted.reduce((s, i) => s + baseW[i], 0);
  const glen = sorted.length;
  let targetSum = Math.max(glen, Math.round(sumG + deltaSigned));
  const hasDistance = sorted.some((i) => {
    const row = segmentRows[i];
    return row && String(row.split_measure_mode || '').toLowerCase() === 'distance';
  });
  const snapShift = shiftSnap && !hasDistance;
  if (snapShift) {
    targetSum = Math.max(glen, nearestStep(targetSum, DRAG_SNAP_STEP));
  }
  return redistributeGroupWeights(baseW, sorted, targetSum);
}

function SortableBarColumn({
  id,
  flexGrow,
  heightPx,
  seg,
  idx,
  vizForBar,
  fillPct,
  title,
  segments,
  setSegments,
  duplicateSegment,
  onDeleteGraphicSegments,
  detailOpen,
  openDetailForIndex,
  closeDetail,
  clearSelection,
  chartRowElRef,
  selectedSet,
  toggleSelectIdx,
  noteBarAnchor,
  distanceUnits,
  thresholdPaceSecPerKm,
  showBarCpRangeLabels,
  resolvedCpWatts,
  heatmapBar,
  t,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const sortableListeners = listeners ?? {};
  const { onPointerDown: sortablePointerDown, ...restSortableListeners } = sortableListeners;
  const columnRef = useRef(null);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    flex: `${flexGrow} 1 0`,
    minWidth: 0,
  };

  const cpDragRef = useRef(null);
  const resizeRef = useRef(null);
  const isSelected = selectedSet.has(idx);
  const endlessProps = endlessBarProps(seg);

  const applyResizeFromRef = useCallback(
    (r, clientX, shiftKey) => {
      if (!r) return;
      const totalDx = clientX - r.anchorX;
      const base = r.startWeightsAll;
      let newW = base.slice();

      if (r.resizeMode === 'pair') {
        const a = r.pairLeft;
        const b = r.pairRight;
        const wi = base[a];
        const wj = base[b];
        const pairSum = wi + wj;
        const d = Math.round((totalDx / r.chartWidthStart) * pairSum);
        if (r.edge === 'right') {
          let na = Math.max(1, Math.min(pairSum - 1, wi + d));
          if (shiftKey) {
            const rowA = segments[a];
            const rowB = segments[b];
            const aDur = String(rowA?.split_measure_mode || 'duration').toLowerCase() !== 'distance';
            const bDur = String(rowB?.split_measure_mode || 'duration').toLowerCase() !== 'distance';
            if (aDur && bDur) {
              na = Math.max(1, Math.min(pairSum - 1, nearestStep(na, DRAG_SNAP_STEP)));
            }
          }
          newW[a] = na;
          newW[b] = pairSum - na;
        } else {
          let nb = Math.max(1, Math.min(pairSum - 1, wj - d));
          if (shiftKey) {
            const rowA = segments[a];
            const rowB = segments[b];
            const aDur = String(rowA?.split_measure_mode || 'duration').toLowerCase() !== 'distance';
            const bDur = String(rowB?.split_measure_mode || 'duration').toLowerCase() !== 'distance';
            if (aDur && bDur) {
              nb = Math.max(1, Math.min(pairSum - 1, nearestStep(nb, DRAG_SNAP_STEP)));
            }
          }
          newW[b] = nb;
          newW[a] = pairSum - nb;
        }
      } else {
        let deltaSigned = Math.round((totalDx / r.chartWidthStart) * r.sumWStart);
        if (r.edge === 'left') deltaSigned = -deltaSigned;
        newW = applyGroupDurationDelta(base, r.groupIndices, deltaSigned, shiftKey, segments);
      }

      const userU = distanceUnits === 'miles' ? 'miles' : 'km';
      setSegments((prev) => prev.map((row, i) => applyWeightToRow(row, newW[i], shiftKey, userU, thresholdPaceSecPerKm)));
    },
    [setSegments, segments, distanceUnits, thresholdPaceSecPerKm]
  );

  const onResizePointerDown = (edge) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    noteBarAnchor?.(idx);
    const chartEl = chartRowElRef?.current;
    if (!chartEl) return;
    const chartRect = chartEl.getBoundingClientRect();
    const n = segments.length;
    const groupIndices = fullResizeGroup(selectedSet, idx, n);
    let resizeMode = 'group';
    let pairLeft = null;
    let pairRight = null;
    if (edge === 'right' && idx < n - 1) {
      const j = idx + 1;
      if (groupIndices.includes(idx) && groupIndices.includes(j)) {
        resizeMode = 'pair';
        pairLeft = idx;
        pairRight = j;
      }
    } else if (edge === 'left' && idx > 0) {
      const j = idx - 1;
      if (groupIndices.includes(idx) && groupIndices.includes(j)) {
        resizeMode = 'pair';
        pairLeft = j;
        pairRight = idx;
      }
    }
    const startWeightsAll = segments.map((row) => splitWeight(segmentRowToVizSplit(row), thresholdPaceSecPerKm));
    const sumWStart = startWeightsAll.reduce((a, b) => a + b, 0) || 1;
    resizeRef.current = {
      pointerId: e.pointerId,
      anchorX: e.clientX,
      edge,
      resizeMode,
      pairLeft,
      pairRight,
      groupIndices,
      startWeightsAll,
      sumWStart,
      chartWidthStart: Math.max(1, chartRect.width),
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onResizePointerMove = (e) => {
    const r = resizeRef.current;
    if (!r || e.pointerId !== r.pointerId) return;
    applyResizeFromRef(r, e.clientX, e.shiftKey);
  };

  const onResizePointerUp = (e) => {
    const r = resizeRef.current;
    if (r && e.pointerId === r.pointerId && e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    resizeRef.current = null;
  };

  const onBarPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey) {
      noteBarAnchor?.(idx);
      toggleSelectIdx(idx);
      return;
    }
    noteBarAnchor?.(idx);
    const indices =
      selectedSet.size > 0 && selectedSet.has(idx) ? [...selectedSet].sort((a, b) => a - b) : [idx];
    const startCps = {};
    const startFillByIdx = {};
    const startCpRangeByIdx = {};
    for (const i of indices) {
      const row = segments[i];
      if (!row) continue;
      startCps[i] = readCp(row);
      startFillByIdx[i] = graphicEditorIntensityFillPercent(row);
      startCpRangeByIdx[i] = {
        center: readCp(row),
        cp_percent_min: row.cp_percent_min,
        cp_percent_max: row.cp_percent_max,
      };
    }
    const intensityBandPx = Math.max(32, heightPx * GRAPHIC_EDITOR_INTENSITY_FRAC);
    cpDragRef.current = {
      pointerId: e.pointerId,
      anchorY: e.clientY,
      startCps,
      startCpRangeByIdx,
      indices,
      intensityBandPx,
      startFillByIdx,
      moved: false,
      t0: Date.now(),
      startX: e.clientX,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onBarPointerMove = (e) => {
    const c = cpDragRef.current;
    if (!c || e.pointerId !== c.pointerId) return;
    const totalDy = c.anchorY - e.clientY;
    const dx = e.clientX - c.startX;
    if (Math.abs(totalDy) > CLICK_MAX_MOVE || Math.abs(dx) > CLICK_MAX_MOVE) c.moved = true;
    const deltaCpBase = (totalDy / c.intensityBandPx) * GRAPHIC_EDITOR_CP_MAX;
    setSegments((prev) => {
      const next = [...prev];
      for (const i of c.indices) {
        const base = c.startCps[i];
        if (base == null) continue;
        const row = next[i];
        const ic = (row.run_intensity_class || 'work').toLowerCase();
        const fill0 = c.startFillByIdx[i] ?? 0;
        const heightScale = 0.35 + 0.65 * Math.min(1.15, fill0 / 100 + 0.12);
        const delta = deltaCpBase * heightScale;

        const snap = c.startCpRangeByIdx[i];
        if (ic === 'rest') {
          let nextCp = Math.round(Math.min(CP_MAX, Math.max(CP_MIN, base + delta)));
          if (e.shiftKey) {
            nextCp = nearestStep(nextCp, DRAG_SNAP_STEP);
            nextCp = Math.min(CP_MAX, Math.max(CP_MIN, nextCp));
          }
          if (nextCp <= 0) {
            next[i] = {
              ...row,
              run_intensity_class: 'rest',
              cp_percent: 0,
              cp_percent_min: '',
              cp_percent_max: '',
            };
          } else {
            next[i] = mergeCpPercentDragWithRangeShift(
              { ...row, run_intensity_class: 'work' },
              nextCp,
              snap
            );
          }
          continue;
        }

        let nextCp = Math.round(Math.min(CP_MAX, Math.max(CP_MIN, base + delta)));
        if (e.shiftKey) {
          nextCp = nearestStep(nextCp, DRAG_SNAP_STEP);
          nextCp = Math.min(CP_MAX, Math.max(CP_MIN, nextCp));
        }
        next[i] = mergeCpPercentDragWithRangeShift(row, nextCp, snap);
      }
      return next;
    });
  };

  const onBarPointerUp = (e) => {
    const c = cpDragRef.current;
    if (!c || e.pointerId !== c.pointerId) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const elapsed = Date.now() - c.t0;
    const shouldOpen = !c.moved && elapsed < CLICK_MAX_MS;
    const moved = c.moved;
    const indices = c.indices;
    cpDragRef.current = null;
    if (moved && indices?.length) {
      setSegments((prev) => {
        const next = [...prev];
        for (const j of indices) {
          const row = next[j];
          if (!row) continue;
          const ic = (row.run_intensity_class || 'work').toLowerCase();
          if (ic === 'rest') continue;
          if (readCp(row) <= 0) {
            next[j] = {
              ...row,
              run_intensity_class: 'rest',
              cp_percent: 0,
              cp_percent_min: '',
              cp_percent_max: '',
            };
          }
        }
        return next;
      });
    }
    if (shouldOpen) {
      openDetailForIndex(idx);
    }
  };

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        columnRef.current = el;
      }}
      data-graphic-seg=""
      {...(isSelected ? { 'data-graphic-seg-selected': '' } : {})}
      style={style}
      className={cn(
        'relative flex min-w-[2px] flex-col self-stretch overflow-hidden rounded-sm transition-colors',
        isSelected && 'bg-primary/12 dark:bg-primary/20',
        isDragging && 'z-20 opacity-[0.97]'
      )}
      onPointerDown={(ev) => ev.stopPropagation()}
    >
      <Popover
        modal
        open={detailOpen}
        onOpenChange={(open) => {
          if (!open) closeDetail();
        }}
      >
        <div
          className="relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden"
          style={{ height: heightPx }}
        >
          <div
            className="z-[4] min-h-0 shrink-0 cursor-grab touch-none hover:bg-muted/20"
            style={{ flex: `0 0 ${GRAPHIC_EDITOR_GRAB_FRAC * 100}%` }}
            {...attributes}
            {...restSortableListeners}
            title={t('runningExercise.dragToReorder')}
            onPointerDown={(ev) => {
              sortablePointerDown?.(ev);
              ev.stopPropagation();
            }}
          />
          <div className="relative z-[6] flex min-h-0 w-full flex-1 flex-col justify-end">
            {!!showBarCpRangeLabels && (
              <div
                className="mb-0.5 line-clamp-2 w-full shrink-0 px-0.5 text-center text-[9px] font-semibold leading-tight text-foreground tabular-nums [text-shadow:0_0_6px_hsl(var(--background)),0_0_2px_hsl(var(--background))]"
                title={formatGraphicBarCpLabel(seg, resolvedCpWatts)}
              >
                {formatGraphicBarCpLabel(seg, resolvedCpWatts)}
              </div>
            )}
            <PopoverAnchor asChild>
              <div
                className={cn(
                  'relative flex h-full min-h-0 w-full min-w-0 flex-row items-stretch rounded-[1px]',
                  !(heatmapBar || endlessProps.bgClass !== undefined) && runningSegmentBarColorClass(seg.run_intensity_class),
                  endlessProps.bgClass,
                )}
                style={{
                  height: `${fillPct}%`,
                  minHeight: 2,
                  maxHeight: '100%',
                  ...endlessProps.style,
                  ...(heatmapBar && !seg.endless
                    ? { backgroundColor: cpPercentHeatmapColor(graphicEditorEffectiveCp(seg)) }
                    : {}),
                }}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={t('runningExercise.resizeDurationLeft')}
                  className="z-[7] w-2 shrink-0 cursor-ew-resize touch-none border-0 bg-transparent p-0 opacity-0"
                  onPointerDown={onResizePointerDown('left')}
                  onPointerMove={onResizePointerMove}
                  onPointerUp={onResizePointerUp}
                  onPointerCancel={onResizePointerUp}
                />
                <div
                  role="button"
                  tabIndex={0}
                  className="z-[6] min-h-0 min-w-0 flex-1 cursor-ns-resize touch-none select-none outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                  title={title}
                  onPointerDown={onBarPointerDown}
                  onPointerMove={onBarPointerMove}
                  onPointerUp={onBarPointerUp}
                  onPointerCancel={onBarPointerUp}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      openDetailForIndex(idx);
                    }
                  }}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={t('runningExercise.resizeDurationRight')}
                  className="z-[7] w-2 shrink-0 cursor-ew-resize touch-none border-0 bg-transparent p-0 opacity-0"
                  onPointerDown={onResizePointerDown('right')}
                  onPointerMove={onResizePointerMove}
                  onPointerUp={onResizePointerUp}
                  onPointerCancel={onResizePointerUp}
                />
              </div>
            </PopoverAnchor>
          </div>
        </div>
        <PopoverContent
          className="max-h-[min(78vh,32rem)] w-[min(96vw,680px)] overflow-y-auto p-3 sm:p-4"
          align="center"
          side="top"
          sideOffset={28}
          collisionPadding={16}
          avoidCollisions
          sticky="partial"
          data-graphic-seg-detail=""
          onOpenAutoFocus={(ev) => ev.preventDefault()}
          onInteractOutside={(ev) => {
            if (ev.target.closest?.('[data-radix-select-viewport]')) {
              ev.preventDefault();
            }
          }}
        >
          <p className="mb-2 text-xs font-medium text-foreground">{t('runningExercise.segmentDetails')}</p>
          <SegmentDetailForm
            seg={seg}
            idx={idx}
            t={t}
            setSegments={setSegments}
            duplicateSegment={duplicateSegment}
            onDeleteGraphicSegments={onDeleteGraphicSegments}
            onClose={closeDetail}
            distanceUnits={distanceUnits}
            thresholdPaceSecPerKm={thresholdPaceSecPerKm}
            resolvedCpWatts={resolvedCpWatts}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Graphic workout strip: flex widths match RunningWorkoutViz; bar fill uses 0–150% CP in the lower band
 * (22% top strip = reorder). 100% CP reference line is aligned to that scale. Card preview still uses RunningWorkoutViz.
 */
export default function RunningWorkoutSegmentsGraphicEditor({
  segments,
  setSegments,
  duplicateSegment,
  heightPx = 168,
  /** When true (e.g. library/diary editor modal), grow the bar strip to fill the flex slot; height is measured via ResizeObserver. */
  fillContainerHeight = false,
  thresholdPaceSecPerKm = null,
  distanceUnits = 'km',
  /** When true, show %CP (and min–max when set) above each bar. */
  showBarCpRangeLabels = false,
  /** Resolved athlete CP in watts; when set, bar labels also show absolute watts. */
  resolvedCpWatts = null,
}) {
  const { t } = useLanguage();
  const [detailIdx, setDetailIdx] = useState(null);
  const [selectedIdxs, setSelectedIdxs] = useState(() => new Set());
  const [measuredBarPx, setMeasuredBarPx] = useState(null);
  const chartSlotRef = useRef(null);
  const chartRowRef = useRef(null);
  const marqueeBandRef = useRef(null);
  const marqueeDragRef = useRef(null);
  const lastAnchorIdxRef = useRef(null);
  const copyBufferRef = useRef(null);
  const [marqueeBox, setMarqueeBox] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const scrollContainerRef = useRef(null);

  const selectedSet = selectedIdxs;
  const clearSelection = useCallback(() => setSelectedIdxs(new Set()), []);
  const toggleSelectIdx = useCallback((idx) => {
    setSelectedIdxs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const noteBarAnchor = useCallback((idx) => {
    lastAnchorIdxRef.current = idx;
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      return idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : prev;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      return idx > 0 ? ZOOM_LEVELS[idx - 1] : prev;
    });
  }, []);

  const zoomReset = useCallback(() => setZoomLevel(1), []);

  const openDetailForIndex = useCallback((i) => {
    lastAnchorIdxRef.current = i;
    setDetailIdx(i);
  }, []);

  const removeSegmentsAtIndices = useCallback(
    (removedInput) => {
      const removed = removedInput instanceof Set ? removedInput : new Set(removedInput);
      const valid = [...removed].filter((i) => Number.isInteger(i) && i >= 0 && i < segments.length);
      if (valid.length === 0) return;
      const removedSet = new Set(valid);

      setSegments((prev) => prev.filter((_, i) => !removedSet.has(i)));
      setDetailIdx((cur) => {
        if (cur == null) return null;
        if (removedSet.has(cur)) return null;
        return remapIndexAfterRemove(cur, removedSet);
      });
      setSelectedIdxs(new Set());

      const a = lastAnchorIdxRef.current;
      if (a != null) {
        let mapped = remapIndexAfterRemove(a, removedSet);
        if (mapped != null) {
          lastAnchorIdxRef.current = mapped;
        } else {
          let c = a - 1;
          while (c >= 0 && removedSet.has(c)) c -= 1;
          if (c >= 0) {
            lastAnchorIdxRef.current = remapIndexAfterRemove(c, removedSet);
          } else {
            const firstSurvivor = segments.findIndex((_, i) => !removedSet.has(i));
            lastAnchorIdxRef.current =
              firstSurvivor >= 0 ? remapIndexAfterRemove(firstSurvivor, removedSet) : null;
          }
        }
      }
    },
    [segments, setSegments]
  );

  const onDeleteGraphicSegments = useCallback(
    (detailIdx) => {
      const toRemove =
        selectedIdxs.size > 0 && selectedIdxs.has(detailIdx)
          ? new Set(selectedIdxs)
          : new Set([detailIdx]);
      removeSegmentsAtIndices(toRemove);
    },
    [selectedIdxs, removeSegmentsAtIndices]
  );

  const copySelectedSegments = useCallback(() => {
    if (selectedIdxs.size === 0) return;
    const order = [...selectedIdxs].sort((a, b) => a - b);
    copyBufferRef.current = order.map((i) => ({ ...segments[i] }));
  }, [segments, selectedIdxs]);

  const pasteBufferedSegments = useCallback(() => {
    const buf = copyBufferRef.current;
    if (!buf?.length) return;
    const clones = buf.map(cloneSegmentRowForPaste);
    const n = clones.length;
    let insertAt = 0;
    setSegments((prev) => {
      let at;
      if (selectedIdxs.size > 0) {
        at = Math.max(...selectedIdxs) + 1;
      } else {
        const a = lastAnchorIdxRef.current;
        at = a == null ? prev.length : a + 1;
      }
      at = Math.min(Math.max(0, at), prev.length);
      insertAt = at;
      const next = [...prev];
      next.splice(at, 0, ...clones);
      return next;
    });
    setSelectedIdxs(new Set(Array.from({ length: n }, (_, i) => insertAt + i)));
    lastAnchorIdxRef.current = insertAt + n - 1;
    setDetailIdx((cur) => {
      if (cur == null) return null;
      if (cur >= insertAt) return cur + n;
      return cur;
    });
  }, [selectedIdxs, setSegments]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const vizSplits = segments.map(segmentRowToVizSplit);
  const weights = vizSplits.map((s) => splitWeight(s, thresholdPaceSecPerKm));
  const sumW = weights.reduce((a, b) => a + b, 0) || 1;
  const cp100LineBottomPct = graphicEditorCp100LineBottomPercentOfColumn();
  const zoomStyle = zoomLevel > 1 ? { minWidth: `${zoomLevel * 100}%` } : undefined;

  const barHeightPx = fillContainerHeight ? Math.max(120, measuredBarPx ?? heightPx) : heightPx;

  useLayoutEffect(() => {
    if (!fillContainerHeight) {
      setMeasuredBarPx(null);
      return undefined;
    }
    const el = chartSlotRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height;
      if (h != null && h > 0) {
        setMeasuredBarPx(Math.max(120, Math.floor(h)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillContainerHeight]);

  useEffect(() => {
    if (selectedIdxs.size === 0) return undefined;
    const onPointerDownCapture = (e) => {
      if (e.button !== 0) return;
      if (e.shiftKey) return;
      const el = e.target;
      if (el.closest?.('[data-graphic-seg-detail]')) return;
      if (el.closest?.('[data-radix-select-viewport]')) return;
      if (el.closest?.('[data-graphic-seg-selected]')) return;
      if (el.closest?.('[data-graphic-marquee-band]')) return;
      clearSelection();
    };
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => document.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, [selectedIdxs.size, clearSelection]);

  useEffect(() => {
    const typingTarget = (el) => {
      if (!el?.closest) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return !!el.closest('[contenteditable="true"]');
    };
    const onKeyDown = (e) => {
      if (typingTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey) {
        const k = e.key?.toLowerCase?.();
        if (k === 'c') {
          if (selectedIdxs.size === 0) return;
          e.preventDefault();
          copySelectedSegments();
        } else if (k === 'v') {
          if (!copyBufferRef.current?.length) return;
          e.preventDefault();
          pasteBufferedSegments();
        }
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selectedIdxs.size === 0) return;
        e.preventDefault();
        removeSegmentsAtIndices(new Set(selectedIdxs));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [copySelectedSegments, pasteBufferedSegments, removeSegmentsAtIndices, selectedIdxs]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = segments.findIndex((s) => s.localKey === active.id);
    const newIndex = segments.findIndex((s) => s.localKey === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setSegments((prev) => arrayMove(prev, oldIndex, newIndex));
    setDetailIdx((cur) => {
      if (cur == null) return null;
      if (cur === oldIndex) return newIndex;
      if (oldIndex < newIndex && cur > oldIndex && cur <= newIndex) return cur - 1;
      if (oldIndex > newIndex && cur >= newIndex && cur < oldIndex) return cur + 1;
      return cur;
    });
    setSelectedIdxs((prev) => {
      const mapIdx = (i) => {
        if (i === oldIndex) return newIndex;
        if (oldIndex < newIndex && i > oldIndex && i <= newIndex) return i - 1;
        if (oldIndex > newIndex && i >= newIndex && i < oldIndex) return i + 1;
        return i;
      };
      const next = new Set();
      for (const i of prev) next.add(mapIdx(i));
      return next;
    });
    if (lastAnchorIdxRef.current != null) {
      const mapIdx = (i) => {
        if (i === oldIndex) return newIndex;
        if (oldIndex < newIndex && i > oldIndex && i <= newIndex) return i - 1;
        if (oldIndex > newIndex && i >= newIndex && i < oldIndex) return i + 1;
        return i;
      };
      lastAnchorIdxRef.current = mapIdx(lastAnchorIdxRef.current);
    }
  };

  if (segments.length === 0) {
    return (
      <div
        className={cn(
          'rounded-md border border-border/60 bg-muted/40 text-sm text-muted-foreground',
          fillContainerHeight
            ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden'
            : 'w-full text-center'
        )}
      >
        <div
          ref={fillContainerHeight ? chartSlotRef : undefined}
          className={cn(fillContainerHeight && 'flex min-h-[120px] flex-1 flex-col justify-center')}
        >
          <div
            className={cn(
              'flex w-full items-center justify-center rounded-md border border-dashed border-border/50 bg-muted/20 px-2 text-center',
              !fillContainerHeight && 'w-full'
            )}
            style={{ minHeight: barHeightPx, height: barHeightPx }}
          >
            {t('runningExercise.graphicEmpty')}
          </div>
        </div>
      </div>
    );
  }

  const graphicBelowChart = (
    <>
      <div
        ref={marqueeBandRef}
        data-graphic-marquee-band=""
        className="relative h-3 w-full min-w-0 shrink-0 cursor-crosshair rounded-sm border border-border/40 bg-muted/30 px-0.5"
        title={t('runningExercise.graphicMarqueeBandTitle')}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          const bandEl = marqueeBandRef.current;
          const chartEl = chartRowRef.current;
          if (!bandEl || !chartEl) return;
          marqueeDragRef.current = {
            pointerId: e.pointerId,
            originX: e.clientX,
            originY: e.clientY,
            bandEl,
            chartEl,
            dragging: false,
          };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const r = marqueeDragRef.current;
          if (!r || e.pointerId !== r.pointerId) return;
          const dx = Math.abs(e.clientX - r.originX);
          const dy = Math.abs(e.clientY - r.originY);
          if (!r.dragging && (dx >= MARQUEE_DRAG_PX || dy >= MARQUEE_DRAG_PX)) {
            r.dragging = true;
          }
          if (!r.dragging) return;
          const idxs = segmentIndicesOverlappingClientXRange(
            chartRowRef.current,
            r.originX,
            e.clientX,
            weights,
            sumW
          );
          setSelectedIdxs(new Set(idxs));
          const bandR = r.bandEl.getBoundingClientRect();
          const lo = Math.min(r.originX, e.clientX);
          const hi = Math.max(r.originX, e.clientX);
          const left = Math.max(0, lo - bandR.left);
          const right = Math.min(bandR.width, hi - bandR.left);
          setMarqueeBox({ left, width: Math.max(0, right - left) });
        }}
        onPointerUp={(e) => {
          const r = marqueeDragRef.current;
          if (!r || e.pointerId !== r.pointerId) return;
          if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
          if (!r.dragging) {
            const i = segmentIndexAtClientX(chartRowRef.current, r.originX, weights, sumW);
            if (i != null) lastAnchorIdxRef.current = i;
            clearSelection();
          } else {
            const idxs = segmentIndicesOverlappingClientXRange(
              chartRowRef.current,
              r.originX,
              e.clientX,
              weights,
              sumW
            );
            if (idxs.length) lastAnchorIdxRef.current = Math.max(...idxs);
          }
          marqueeDragRef.current = null;
          setMarqueeBox(null);
        }}
        onPointerCancel={(e) => {
          const r = marqueeDragRef.current;
          if (r && e.pointerId === r.pointerId && e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
          marqueeDragRef.current = null;
          setMarqueeBox(null);
        }}
      >
        {marqueeBox && (
          <div
            className="pointer-events-none absolute top-0 z-[2] h-full rounded-sm bg-primary/25"
            style={{ left: marqueeBox.left, width: marqueeBox.width }}
          />
        )}
      </div>
      <div className="flex w-full min-w-0 gap-0 px-0.5">
        {segments.map((seg, idx) => {
          const flexGrow = weights[idx] / sumW;
          const { line1, line2, cp } = graphicBarFooterParts(
            seg,
            t,
            thresholdPaceSecPerKm,
            distanceUnits
          );
          const fullMeta = [line1, line2, cp].filter(Boolean).join(' · ');
          return (
            <div
              key={seg.localKey}
              className="flex min-w-0 flex-col items-center justify-start text-[9px] leading-tight text-muted-foreground"
              style={{ flex: `${flexGrow} 1 0`, minWidth: 0 }}
              title={fullMeta}
            >
              <span className="block w-full truncate text-center tabular-nums">{line1}</span>
              {!!line2 && (
                <span className="block w-full truncate text-center tabular-nums">{line2}</span>
              )}
              <span className="block w-full truncate text-center tabular-nums">{cp}</span>
            </div>
          );
        })}
      </div>
    </>
  );

  const zoomControlsRow = (
    <div className="mt-1.5 flex items-start justify-between gap-2">
      <p className="min-w-0 flex-1 text-[11px] text-muted-foreground">
        {t('runningExercise.graphicHint')}
      </p>
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          disabled={zoomLevel <= ZOOM_LEVELS[0]}
          onClick={zoomOut}
          title={t('runningExercise.zoomOut')}
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <button
          type="button"
          className="h-6 min-w-[2rem] rounded px-1 text-center text-[10px] tabular-nums text-muted-foreground hover:text-foreground"
          onClick={zoomReset}
          title={t('runningExercise.zoomReset')}
        >
          {zoomLevel === 1 ? t('runningExercise.zoomFit') : `${zoomLevel}×`}
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          disabled={zoomLevel >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
          onClick={zoomIn}
          title={t('runningExercise.zoomIn')}
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={400}>
    <div
      className={cn(
        'rounded-md border border-dashed border-border/60 bg-muted/10 p-2',
        fillContainerHeight
          ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden'
          : 'overflow-hidden'
      )}
    >
      <div
        ref={scrollContainerRef}
        className={cn(
          'overflow-x-auto',
          fillContainerHeight && 'min-h-0 flex-1 overflow-y-hidden'
        )}
      >
        <div
          className={cn(
            'flex w-full min-w-0 flex-col',
            fillContainerHeight ? 'h-full' : 'gap-1'
          )}
          style={zoomStyle}
        >
          <div
            ref={fillContainerHeight ? chartSlotRef : undefined}
            className={cn(fillContainerHeight && 'flex min-h-[120px] min-w-0 flex-1 flex-col')}
          >
            {/*
              Marquee + footer must stay OUTSIDE DndContext so @dnd-kit pointer sensors do not
              swallow drags on the selection strip (regression when toolbar/modal layout changed).
            */}
            <DndContext sensors={sensors} collisionDetection={chartCollisionDetection} onDragEnd={handleDragEnd}>
              <SortableContext items={segments.map((s) => s.localKey)} strategy={horizontalListSortingStrategy}>
                <div
                  ref={chartRowRef}
                  className={cn(
                    'relative flex w-full min-w-0 items-end gap-0 overflow-hidden rounded-md border border-border/50 bg-muted/20 px-0.5 pb-0.5',
                    fillContainerHeight && 'min-h-0 flex-1'
                  )}
                  style={fillContainerHeight ? undefined : { height: barHeightPx }}
                  role="img"
                  aria-label={t('runningExercise.graphicAria')}
                >
                  <div
                    className="pointer-events-none absolute left-1 z-[25] flex items-center gap-1 pr-1"
                    style={{ bottom: `${cp100LineBottomPct}%`, right: 4 }}
                  >
                    <div className="h-px min-w-0 flex-1 border-t border-dashed border-foreground/50" />
                    <span className="shrink-0 text-[9px] font-medium tabular-nums text-muted-foreground">
                      {t('runningExercise.graphicCp100Line')}
                    </span>
                  </div>
                  {segments.map((seg, idx) => {
                    const vizForBar = vizSplits[idx];
                    const flexGrow = weights[idx] / sumW;
                    const fillPct = graphicEditorIntensityFillPercent(seg);
                    const title = splitTitleParts(vizForBar);
                    const heatmapBar = runningSegmentUsesCpHeatmapBarColor(seg.run_intensity_class);
                    return (
                      <SortableBarColumn
                        key={seg.localKey}
                        id={seg.localKey}
                        flexGrow={flexGrow}
                        heightPx={barHeightPx}
                        seg={seg}
                        idx={idx}
                        vizForBar={vizForBar}
                        fillPct={fillPct}
                        title={title}
                        segments={segments}
                        setSegments={setSegments}
                        duplicateSegment={duplicateSegment}
                        onDeleteGraphicSegments={onDeleteGraphicSegments}
                        detailOpen={detailIdx === idx}
                        openDetailForIndex={openDetailForIndex}
                        closeDetail={() => setDetailIdx(null)}
                        clearSelection={clearSelection}
                        chartRowElRef={chartRowRef}
                        selectedSet={selectedSet}
                        toggleSelectIdx={toggleSelectIdx}
                        noteBarAnchor={noteBarAnchor}
                        distanceUnits={distanceUnits}
                        thresholdPaceSecPerKm={thresholdPaceSecPerKm}
                        showBarCpRangeLabels={showBarCpRangeLabels}
                        resolvedCpWatts={resolvedCpWatts}
                        heatmapBar={heatmapBar}
                        t={t}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          <div className={cn('shrink-0', fillContainerHeight && 'flex flex-col gap-1')}>
            {graphicBelowChart}
          </div>
        </div>
      </div>
      <div className="shrink-0">{zoomControlsRow}</div>
    </div>
    </TooltipProvider>
  );
}
